import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { getApiBase } from "../api";

export default function OilfieldLoadBoard() {
  const apiBase = useMemo(() => getApiBase(), []);
  const [loads, setLoads] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    jobType: "",
    equipmentType: "",
    h2sRequired: "",
    state: ""
  });

  useEffect(() => {
    fetchEquipmentTypes();
    fetchJobTypes();
    fetchLoads();
  }, []);

  async function fetchEquipmentTypes() {
    const res = await fetch(`${apiBase}/oilfield/equipment-types`);
    if (res.ok) {
      const data = await res.json();
      setEquipmentTypes(data.equipmentTypes.filter(e => e.oilfield));
    }
  }

  async function fetchJobTypes() {
    const res = await fetch(`${apiBase}/oilfield/job-types`);
    if (res.ok) {
      const data = await res.json();
      setJobTypes(data.jobTypes);
    }
  }

  async function fetchLoads() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.jobType) params.append("jobType", filters.jobType);
      if (filters.equipmentType) params.append("equipmentType", filters.equipmentType);
      if (filters.h2sRequired) params.append("h2sRequired", filters.h2sRequired);
      if (filters.state) params.append("state", filters.state);

      const res = await fetch(`${apiBase}/oilfield/loads?${params}`);
      if (res.ok) {
        const data = await res.json();
        setLoads(data.loads || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchLoads();
  }, [filters]);

  function formatCurrency(cents) {
    return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0 });
  }

  function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  function getPriorityBadge(priority) {
    const styles = {
      EMERGENCY: { bg: "#fee2e2", color: "#991b1b", label: "🚨 EMERGENCY" },
      HOT: { bg: "#fef3c7", color: "#92400e", label: "🔥 HOT" },
      STANDARD: { bg: "#f3f4f6", color: "#374151", label: "Standard" }
    };
    const s = styles[priority] || styles.STANDARD;
    return (
      <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <PageHeader 
        title="🛢️ Oilfield Load Board" 
        subtitle="Specialized loads for oilfield operations - Permian, Eagle Ford, Bakken & more"
      />

      {/* Filters */}
      <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 16, marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={filters.jobType}
            onChange={e => setFilters({ ...filters, jobType: e.target.value })}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 150 }}
          >
            <option value="">All Job Types</option>
            {jobTypes.map(j => (
              <option key={j.code} value={j.code}>{j.name}</option>
            ))}
          </select>

          <select
            value={filters.equipmentType}
            onChange={e => setFilters({ ...filters, equipmentType: e.target.value })}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db", minWidth: 150 }}
          >
            <option value="">All Equipment</option>
            {equipmentTypes.map(e => (
              <option key={e.code} value={e.code}>{e.name}</option>
            ))}
          </select>

          <select
            value={filters.h2sRequired}
            onChange={e => setFilters({ ...filters, h2sRequired: e.target.value })}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
          >
            <option value="">H2S: Any</option>
            <option value="true">H2S Required</option>
            <option value="false">No H2S</option>
          </select>

          <select
            value={filters.state}
            onChange={e => setFilters({ ...filters, state: e.target.value })}
            style={{ padding: 10, borderRadius: 6, border: "1px solid #d1d5db" }}
          >
            <option value="">All States</option>
            <option value="TX">Texas</option>
            <option value="NM">New Mexico</option>
            <option value="OK">Oklahoma</option>
            <option value="ND">North Dakota</option>
            <option value="CO">Colorado</option>
            <option value="WY">Wyoming</option>
            <option value="LA">Louisiana</option>
          </select>

          <button
            onClick={() => setFilters({ jobType: "", equipmentType: "", h2sRequired: "", state: "" })}
            style={{ padding: "10px 16px", background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer" }}
          >
            Clear Filters
          </button>

          <div style={{ marginLeft: "auto", fontSize: 13, color: "#6b7280" }}>
            {loads.length} loads found
          </div>
        </div>
      </div>

      {/* Certification Notice */}
      <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 8, padding: 12, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ fontSize: 24 }}>⚠️</span>
        <div>
          <strong>Oilfield loads require certifications.</strong> Most sites require SafeLand/PEC training. 
          H2S certification required where indicated. <Link to="/app/carrier/certifications" style={{ color: "#92400e" }}>Manage your certifications →</Link>
        </div>
      </div>

      {/* Load List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>Loading oilfield loads...</div>
      ) : loads.length === 0 ? (
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: 60, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🛢️</div>
          <h3 style={{ margin: 0, fontWeight: 700 }}>No Oilfield Loads Available</h3>
          <p style={{ color: "#6b7280", marginTop: 8 }}>Check back soon or adjust your filters.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {loads.map(load => (
            <div 
              key={load.id}
              style={{ 
                background: "white", 
                borderRadius: 12, 
                border: load.priorityLevel === 'EMERGENCY' ? "2px solid #ef4444" : 
                        load.priorityLevel === 'HOT' ? "2px solid #f59e0b" : "1px solid #e5e7eb",
                padding: 20,
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto",
                gap: 20,
                alignItems: "center"
              }}
            >
              {/* Left - Origin/Dest */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  {getPriorityBadge(load.priorityLevel)}
                  <span style={{ padding: "2px 8px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                    {load.jobType || "OILFIELD"}
                  </span>
                  <span style={{ padding: "2px 8px", background: "#f3f4f6", borderRadius: 4, fontSize: 11 }}>
                    {load.equipmentType}
                  </span>
                </div>

                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
                  {load.originWellsiteName || load.originCity || "Origin"}, {load.originState}
                  <span style={{ margin: "0 8px", color: "#9ca3af" }}>→</span>
                  {load.destWellsiteName || load.destCity || "Destination"}, {load.destState}
                </div>

                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {load.originFieldName && <span style={{ marginRight: 12 }}>📍 {load.originFieldName}</span>}
                  {load.rigName && <span style={{ marginRight: 12 }}>🏗️ {load.rigName}</span>}
                  {load.shipperName && <span>🏢 {load.shipperName}</span>}
                </div>
              </div>

              {/* Middle - Requirements */}
              <div>
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <strong>Pickup:</strong> {formatDate(load.pickupStart)}
                  {load.is24x7 && <span style={{ marginLeft: 8, color: "#10b981" }}>24/7 Operations</span>}
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {load.h2sRequired && (
                    <span style={{ padding: "2px 6px", background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                      ☠️ H2S REQUIRED
                    </span>
                  )}
                  {load.safelandRequired && (
                    <span style={{ padding: "2px 6px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 10 }}>
                      SafeLand
                    </span>
                  )}
                  {load.pecRequired && (
                    <span style={{ padding: "2px 6px", background: "#dbeafe", color: "#1e40af", borderRadius: 4, fontSize: 10 }}>
                      PEC
                    </span>
                  )}
                  {load.winchRequired && (
                    <span style={{ padding: "2px 6px", background: "#f3f4f6", borderRadius: 4, fontSize: 10 }}>
                      🔧 Winch
                    </span>
                  )}
                  {load.hazmatRequired && (
                    <span style={{ padding: "2px 6px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontSize: 10 }}>
                      ⚠️ HAZMAT
                    </span>
                  )}
                  {load.originH2s && (
                    <span style={{ padding: "2px 6px", background: "#fee2e2", color: "#991b1b", borderRadius: 4, fontSize: 10 }}>
                      H2S on Site
                    </span>
                  )}
                </div>
              </div>

              {/* Right - Rate & Action */}
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: "#059669" }}>
                  {formatCurrency(load.rateAmountCents)}
                </div>
                {load.weightLbs && (
                  <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                    {load.weightLbs.toLocaleString()} lbs
                  </div>
                )}
                <Link 
                  to={`/carrier/loads/${load.id}`}
                  style={{
                    display: "inline-block",
                    padding: "8px 16px",
                    background: "#2563eb",
                    color: "white",
                    borderRadius: 6,
                    textDecoration: "none",
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
